import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { Session } from '../types/session';

interface CheckInEvent {
  id: string;
  attendeeId: string;
  attendeeName: string;
  sessionId: string;
  sessionTitle: string;
  checkedInAt: string;
  message: string;
}

interface AttendanceUpdateEvent {
  sessionId: string;
  count: number;
  timestamp: string;
}

export interface LiveAnnouncement {
  subject: string;
  body: string;
  priority: string;
  imageUrl?: string;
}

interface WebSocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  latestAnnouncement: LiveAnnouncement | null;
  dismissAnnouncement: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  socket: null,
  isConnected: false,
  latestAnnouncement: null,
  dismissAnnouncement: () => {},
});

export const useWebSocket = () => useContext(WebSocketContext);

interface WebSocketProviderProps {
  children: React.ReactNode;
}

const getWebSocketUrl = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

export default function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [latestAnnouncement, setLatestAnnouncement] = useState<LiveAnnouncement | null>(null);
  const queryClient = useQueryClient();

  const dismissAnnouncement = () => setLatestAnnouncement(null);
  const updateBatchRef = useRef<number | null>(null);
  const pendingUpdatesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Get JWT token from localStorage (if available)
    const token = localStorage.getItem('jwt_token');
    
    // Initialize socket connection
    const socketInstance = io(getWebSocketUrl(), {
      auth: {
        token: token || 'public', // Use 'public' for unauthenticated users
      },
      transports: ['polling', 'websocket'], // Start with polling (works through proxies), upgrade to WS
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
    });

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      
      // Subscribe to session updates
      socketInstance.emit('subscribe:sessions');
    });

    socketInstance.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      setIsConnected(false);
    });

    // Session event handlers with batched updates
    socketInstance.on('session:created', (session: Session) => {
      console.log('Session created:', session.id);
      batchUpdate(() => {
        queryClient.setQueryData<Session[]>(['sessions'], (old) => {
          if (!old) return [session];
          return [...old, session];
        });
      });
    });

    socketInstance.on('session:updated', (session: Session) => {
      console.log('Session updated:', session.id);
      batchUpdate(() => {
        queryClient.setQueryData<Session[]>(['sessions'], (old) => {
          if (!old) return [session];
          return old.map((s) => (s.id === session.id ? session : s));
        });
        
        // Also update individual session query if it exists
        queryClient.setQueryData(['sessions', session.id], session);
      });
    });

    socketInstance.on('session:deleted', (data: { id: string }) => {
      console.log('Session deleted:', data.id);
      batchUpdate(() => {
        queryClient.setQueryData<Session[]>(['sessions'], (old) => {
          if (!old) return [];
          return old.filter((s) => s.id !== data.id);
        });
        
        // Remove individual session query
        queryClient.removeQueries({ queryKey: ['sessions', data.id] });
      });
    });

    // Check-in event handlers
    socketInstance.on('checkin:recorded', (checkIn: CheckInEvent) => {
      console.log('Check-in recorded:', checkIn);
      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['checkins', checkIn.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['attendees', checkIn.attendeeId] });
    });

    // Handle batched check-in events
    socketInstance.on('checkin:recorded:batch', (checkIns: CheckInEvent[]) => {
      console.log('Batched check-ins recorded:', checkIns.length);
      const sessionIds = new Set(checkIns.map(c => c.sessionId));
      const attendeeIds = new Set(checkIns.map(c => c.attendeeId));
      
      sessionIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['checkins', id] });
      });
      attendeeIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['attendees', id] });
      });
    });

    socketInstance.on('attendance:updated', (attendance: AttendanceUpdateEvent) => {
      console.log('Attendance updated:', attendance);
      batchUpdate(() => {
        // Update session list with new attendance count
        queryClient.setQueryData<Session[]>(['sessions'], (old) => {
          if (!old) return old;
          return old.map((session) => {
            if (session.id === attendance.sessionId) {
              return {
                ...session,
                attendanceCount: attendance.count,
              };
            }
            return session;
          });
        });
        
        // Invalidate analytics queries to refetch
        queryClient.invalidateQueries({ queryKey: ['analytics'] });
      });
    });

    // Handle batched attendance updates
    socketInstance.on('attendance:updated:batch', (updates: AttendanceUpdateEvent[]) => {
      console.log('Batched attendance updates:', updates.length);
      batchUpdate(() => {
        // Update session list with new attendance counts
        queryClient.setQueryData<Session[]>(['sessions'], (old) => {
          if (!old) return old;
          const updateMap = new Map(updates.map(u => [u.sessionId, u.count]));
          return old.map((session) => {
            const newCount = updateMap.get(session.id);
            if (newCount !== undefined) {
              return {
                ...session,
                attendanceCount: newCount,
              };
            }
            return session;
          });
        });
        
        // Invalidate analytics queries to refetch
        queryClient.invalidateQueries({ queryKey: ['analytics'] });
      });
    });

    // Announcement handler
    socketInstance.on('announcement:new', (announcement: LiveAnnouncement) => {
      console.log('Announcement received:', announcement.subject);
      setLatestAnnouncement(announcement);
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      if (updateBatchRef.current) {
        clearTimeout(updateBatchRef.current);
      }
      socketInstance.disconnect();
    };
  }, [queryClient]);

  // Batch updates to minimize UI disruption
  const batchUpdate = (updateFn: () => void) => {
    const updateId = Math.random().toString();
    pendingUpdatesRef.current.add(updateId);

    if (updateBatchRef.current) {
      clearTimeout(updateBatchRef.current);
    }

    updateBatchRef.current = setTimeout(() => {
      // Execute all pending updates
      updateFn();
      pendingUpdatesRef.current.clear();
      updateBatchRef.current = null;
    }, 100); // 100ms batch window
  };

  return (
    <WebSocketContext.Provider value={{ socket, isConnected, latestAnnouncement, dismissAnnouncement }}>
      {children}
    </WebSocketContext.Provider>
  );
}
